"use client";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useAuth } from "../../../hooks/useAuth";
import { channelsAPI, handleApiError } from "../../../services/api";
import ConfirmModal from "./ConfirmModal";
import "./ManageMembersModal.css";

const ManageMembersModal = ({ isOpen, onClose, channelId, channelName, userRole }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUserType, user } = useAuth();
  
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8',
      'VETERINARIAN': '#00D4B3',
      'SERVICE_PROVIDER': '#D65745',
      'CLINIC_MODERATOR': '#2A8AD2',
    };
    return userTypeConfig[userType] || '#8548A8';
  };
  const userTypeColor = getUserTypeColor(currentUserType);
  
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingMemberId, setUpdatingMemberId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  
  const [confirmModalState, setConfirmModalState] = useState({
    isOpen: false,
    memberId: null,
    memberName: '',
    action: null, // 'promote', 'demote', 'kick'
  });

  const mapMember = (member) => {
    const userData = member.user || {};
    const userId = userData.user_id || userData.id || member.user_id || member.id;
    const isSelf = userId === (user?.id || user?.user_id);
    
    // Get fallback name for current user from auth context
    const authUserName = isSelf ? (user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.username) : null;

    // Robust name resolution
    const resolvedName = 
      userData.name || 
      [userData.first_name, userData.last_name].filter(Boolean).join(" ").trim() ||
      userData.username ||
      member.name || // Check if flattened on member object
      [member.first_name, member.last_name].filter(Boolean).join(" ").trim() ||
      member.username ||
      authUserName ||
      "Unknown";

    return {
      // Use the actual user id, since backend expects /members/{userId}/role
      id: userId,
      name: resolvedName,
      profileImage: userData.profile_image_url || userData.avatar || member.profile_image_url || member.avatar || (isSelf ? user?.profile_image_url || user?.avatar : null) || "/icons/person.png",
      role: member.role || "member",
      userType: userData.user_type || userData.type || member.user_type || member.type || (isSelf ? user?.user_type || user?.type : null) || "PET_GUARDIAN",
    };
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const membersResponse = await channelsAPI.getChannelMembers(channelId, {
          page: 1,
          per_page: 100,
          query: debouncedSearchTerm,
        });

        if (cancelled) {
          return;
        }

        if (membersResponse?.data?.data) {
          setMembers(membersResponse.data.data.map(mapMember));
        } else {
          setMembers([]);
        }
      } catch (error) {
        if (!cancelled) {
          handleApiError(error);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [isOpen, channelId, debouncedSearchTerm]);

  const requestAction = (memberId, memberName, action) => {
    setConfirmModalState({
      isOpen: true,
      memberId,
      memberName,
      action
    });
  };

  const executeConfirmedAction = async () => {
    const { action, memberId } = confirmModalState;
    if (!action || !memberId) return;

    setUpdatingMemberId(memberId);
    setConfirmModalState(prev => ({ ...prev, isOpen: false }));

    try {
      if (action === 'promote') {
        await channelsAPI.assignAdmin(channelId, memberId);
        setMembers(prev => prev.map(member => 
          member.id === memberId ? { ...member, role: 'admin' } : member
        ));
      } else if (action === 'demote') {
        await channelsAPI.removeAdmin(channelId, memberId);
        setMembers(prev => prev.map(member => 
          member.id === memberId ? { ...member, role: 'member' } : member
        ));
      } else if (action === 'kick') {
        await channelsAPI.removeMember(channelId, memberId);
        setMembers(prev => prev.filter(member => member.id !== memberId));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setUpdatingMemberId(null);
    }
  };


  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return '#F59E0B';
      case 'admin':
        return '#3B82F6';
      default:
        return '#6B7280';
    }
  };

  const currentUserId = user?.id || user?.user_id;

  const owner = members.find(m => m.role === 'owner');
  const admins = members.filter(m => m.role === 'admin');
  const regularMembers = members.filter(m => m.role === 'member');
  const isOwner = userRole === 'owner';
  const isAdmin = userRole === 'admin';

  const getConfirmModalProps = () => {
    const { action, memberName } = confirmModalState;
    switch (action) {
      case 'promote':
        return {
          title: t("manageMembers.confirm.promoteTitle", "Promote Member"),
          message: t("manageMembers.confirm.promoteMessage", "Are you sure you want to promote {{name}} to Moderator?", { name: memberName }),
          confirmLabel: t("manageMembers.confirm.promoteConfirm", "Promote"),
          showWarningIcon: false
        };
      case 'demote':
        return {
          title: t("manageMembers.confirm.demoteTitle", "Demote Moderator"),
          message: t("manageMembers.confirm.demoteMessage", "Are you sure you want to demote {{name}} to a regular member?", { name: memberName }),
          confirmLabel: t("manageMembers.confirm.demoteConfirm", "Demote"),
          showWarningIcon: true
        };
      case 'kick':
        return {
          title: t("manageMembers.confirm.kickTitle", "Kick Member"),
          message: t("manageMembers.confirm.kickMessage", "Are you sure you want to remove {{name}} from the community?", { name: memberName }),
          confirmLabel: t("manageMembers.confirm.kickConfirm", "Remove"),
          showWarningIcon: true
        };
      default:
        return {};
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="manage-members-modal-overlay" onClick={onClose}>
      <div className="manage-members-modal" onClick={(e) => e.stopPropagation()} style={{ '--user-type-color': userTypeColor }}>
        <div className="manage-members-modal-header">
          <div className="manage-members-modal-header-content">
            <h2 className="manage-members-modal-title">{t("manageMembers.membersTitle", "Members")}</h2>
            <span className="manage-members-modal-subtitle">
              {members.length}{" "}
              {members.length === 1
                ? t("manageMembers.singleMember", "member")
                : t("manageMembers.multiMember", "members")}
            </span>
          </div>
          <div className="manage-members-header-search">
            <div className="manage-members-search-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input 
                type="text" 
                placeholder={t("manageMembers.searchPlaceholder", "Search members...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <button className="manage-members-modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="manage-members-modal-content">
          {isLoading ? (
            <div className="manage-members-modal-loading">
              <div className="loading-spinner"></div>
              <p>{t("manageMembers.loading", "Loading members...")}</p>
            </div>
          ) : (
            <div className="manage-members-modal-body">
              <div className="manage-members-modal-pane">
                <div className="manage-members-pane-content">
                  {members.length > 0 ? (
                    <div className="manage-members-sections">
                      {/* Owner Section */}
                      {owner && (
                        <div className="manage-members-section">
                          <h3 className="manage-members-section-title">{t("manageMembers.sections.owner", "Community Owner")}</h3>
                          <div className="manage-members-list single-row">
                            <MemberCard 
                              member={owner} 
                              isCurrentUser={owner.id === currentUserId}
                              getRoleBadgeColor={getRoleBadgeColor}
                              onNavigate={() => { navigate(`/profile/${owner.id}`); onClose(); }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Admins / Mods Section */}
                      {admins.length > 0 && (
                        <div className="manage-members-section">
                          <h3 className="manage-members-section-title">{t("manageMembers.sections.admins", "Moderators")}</h3>
                          <div className="manage-members-list">
                            {admins.map(admin => (
                              <MemberCard 
                                key={admin.id}
                                member={admin} 
                                isCurrentUser={admin.id === currentUserId}
                                isOwner={isOwner}
                                isAdmin={isAdmin}
                                getRoleBadgeColor={getRoleBadgeColor}
                                updatingMemberId={updatingMemberId}
                                onNavigate={() => { navigate(`/profile/${admin.id}`); onClose(); }}
                                onAction={() => requestAction(admin.id, admin.name, 'demote')}
                                onKick={() => requestAction(admin.id, admin.name, 'kick')}
                                actionType="demote"
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Regular Members Section */}
                      {regularMembers.length > 0 && (
                        <div className="manage-members-section">
                          <h3 className="manage-members-section-title">{t("manageMembers.sections.members", "Members")}</h3>
                          <div className="manage-members-list">
                            {regularMembers.map(member => (
                              <MemberCard 
                                key={member.id}
                                member={member} 
                                isCurrentUser={member.id === currentUserId}
                                isOwner={isOwner}
                                isAdmin={isAdmin}
                                getRoleBadgeColor={getRoleBadgeColor}
                                updatingMemberId={updatingMemberId}
                                onNavigate={() => { navigate(`/profile/${member.id}`); onClose(); }}
                                onAction={() => requestAction(member.id, member.name, 'promote')}
                                onKick={() => requestAction(member.id, member.name, 'kick')}
                                actionType="promote"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="manage-members-modal-empty">
                      <p>{t("manageMembers.noMembers", "No members found.")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {confirmModalState.isOpen && (
        <ConfirmModal
          isOpen={confirmModalState.isOpen}
          onClose={() => setConfirmModalState(prev => ({ ...prev, isOpen: false }))}
          onCancel={() => setConfirmModalState(prev => ({ ...prev, isOpen: false }))}
          onConfirm={executeConfirmedAction}
          {...getConfirmModalProps()}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

// Sub-component for individual member cards
const MemberCard = ({ 
  member, 
  isCurrentUser, 
  isOwner, 
  isAdmin,
  getRoleBadgeColor,
  updatingMemberId, 
  onNavigate, 
  onAction, 
  onKick,
  actionType 
}) => {
  const { t } = useTranslation();
  const isUpdating = updatingMemberId === member.id;
  const canModifyRole = isOwner && !isCurrentUser && member.role !== 'owner';
  const canKick = (isOwner || (isAdmin && member.role === 'member')) && !isCurrentUser && member.role !== 'owner';

  return (
    <div className="manage-members-member-card">
      <div className="manage-members-card-main" onClick={onNavigate}>
        <img
          src={member.profileImage || "/icons/person.png"}
          alt={member.name}
          className="manage-members-member-avatar"
          onError={e => {
            e.target.src = "/icons/person.png";
          }}
        />
        <span className="manage-members-member-name">{member.name}</span>
        <span
          className="manage-members-member-role-badge"
          style={{ backgroundColor: getRoleBadgeColor(member.role) }}
        >
          {t(`manageMembers.roles.${member.role?.toLowerCase()}`, member.role)}
        </span>
        {isCurrentUser && (
          <span className="manage-members-member-you-badge">
            {t("manageMembers.youLabel", "You")}
          </span>
        )}
      </div>

      {(canModifyRole || canKick) && (
        <div className="manage-members-card-actions">
          {canModifyRole && actionType === 'promote' && (
            <button 
              className="manage-members-action-btn promote"
              onClick={(e) => { e.stopPropagation(); onAction(); }}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <div className="action-spinner"></div>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="18 15 12 9 6 15"></polyline>
                  </svg>
                  <span>{t("manageMembers.actions.promote", "Promote to Mod")}</span>
                </>
              )}
            </button>
          )}
          {canModifyRole && actionType === 'demote' && (
            <button 
              className="manage-members-action-btn demote"
              onClick={(e) => { e.stopPropagation(); onAction(); }}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <div className="action-spinner"></div>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                  <span>{t("manageMembers.actions.demote", "Demote to Member")}</span>
                </>
              )}
            </button>
          )}
          {canKick && (
            <button 
              className="manage-members-action-btn kick"
              onClick={(e) => { e.stopPropagation(); onKick(); }}
              disabled={isUpdating}
              style={{ color: "#EF4444", borderColor: "#EF4444" }}
            >
              {isUpdating ? (
                <div className="action-spinner" style={{ borderColor: "rgba(239, 68, 68, 0.3)", borderTopColor: "#EF4444" }}></div>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12"></path>
                  </svg>
                  <span>{t("manageMembers.actions.kick", "Kick")}</span>
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ManageMembersModal;
